/** Client-side text utilities for rebuild viewer. */

import type { ScriptEdit } from '@/types/rebuild';

export interface HighlightSpan {
  start: number;
  end: number;
  editId: string;
  metricKey: string;
}

/** Map applied AI edits to highlight spans in current_text (approximate by matching revised segments). */
export function highlightSpansForEdits(
  currentText: string,
  edits: ScriptEdit[],
): HighlightSpan[] {
  const applied = edits.filter((e) => e.status === 'applied' && e.source === 'ai');
  const spans: HighlightSpan[] = [];
  let searchFrom = 0;

  for (const edit of applied) {
    const needle = edit.revised_text;
    if (!needle) continue;
    const idx = currentText.indexOf(needle, searchFrom);
    if (idx >= 0) {
      spans.push({
        start: idx,
        end: idx + needle.length,
        editId: edit.id,
        metricKey: edit.metric_key,
      });
      searchFrom = idx + needle.length;
    }
  }
  return spans;
}

export function recomputeFromOriginal(originalText: string, edits: ScriptEdit[]): string {
  const applied = edits
    .filter((e) => e.status === 'applied' && e.source === 'ai')
    .sort((a, b) => b.start_offset - a.start_offset);
  let result = originalText;
  for (const edit of applied) {
    const { start_offset: start, end_offset: end, revised_text: revised } = edit;
    if (start >= 0 && end <= result.length && start <= end) {
      result = result.slice(0, start) + revised + result.slice(end);
    }
  }
  return result;
}

export function splitActionableBullets(actionableNotes: string): string[] {
  if (!actionableNotes?.trim()) return [];
  const bullets: string[] = [];
  for (const line of actionableNotes.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith('- ')) bullets.push(trimmed.slice(2).trim());
    else if (trimmed.startsWith('* ')) bullets.push(trimmed.slice(2).trim());
    else if (/^\d+\.\s+/.test(trimmed)) bullets.push(trimmed.replace(/^\d+\.\s+/, '').trim());
    else bullets.push(trimmed);
  }
  return bullets.filter(Boolean);
}

export type TextSegment = { text: string; highlight?: boolean; editId?: string };

/** Split text into plain and highlighted segments for rendering. */
export function segmentTextWithHighlights(
  text: string,
  spans: HighlightSpan[],
): TextSegment[] {
  if (!spans.length) return [{ text }];
  const sorted = [...spans].sort((a, b) => a.start - b.start);
  const segments: TextSegment[] = [];
  let cursor = 0;

  for (const span of sorted) {
    if (span.start > cursor) {
      segments.push({ text: text.slice(cursor, span.start) });
    }
    if (span.end > span.start) {
      segments.push({
        text: text.slice(span.start, span.end),
        highlight: true,
        editId: span.editId,
      });
    }
    cursor = Math.max(cursor, span.end);
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor) });
  }
  return segments;
}
